
import Vue from 'vue';
import Component from 'vue-class-component';
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import SectionDocumentMetaData from "@/features/shared/components/sections/SectionDocumentMetaData.vue";
import PracticeSeafaringDetailsContent
    from "@/features/documents/practices/components/PracticeSeafaringDetailsContent.vue";
import PracticeShipRepairDetailsContent
    from "@/features/documents/practices/components/PracticeShipRepairDetailsContent.vue";
import PracticeInlandVesselDetailsContent
    from "@/features/documents/practices/components/PracticeInlandVesselDetailsContent.vue";
import PracticePilotageDetailsContent
    from "@/features/documents/practices/components/PracticePilotageDetailsContent.vue";
import PracticeVtsCenterDetailsContent
    from "@/features/documents/practices/components/PracticeVtsCenterDetailsContent.vue";
import {Prop} from "vue-property-decorator";
import {PracticeDetailsProps} from "@/features/documents/practices/PracticeDetailsProps";
import {
    factory,
    IPracticeActions,
    PracticeActions,
    PracticeDetailsModel,
    PracticesClient, ResultError,
    SeafaringPracticeDetailsModel,
    ShipRepairPracticeDetailsModel,
    InlandVesselPracticeDetailsModel,
    PilotagePracticeDetailsModel,
    VtsCenterPracticeDetailsModel
} from "@/api-client";
import ValidityStatusBadge from "@/features/shared/components/ValidityStatusBadge.vue";
import ApprovalActions from "@/features/shared/components/sections/ApprovalActions.vue";
import RejectionDetails from "@/features/shared/components/RejectionDetails.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import ForwardForConfirmationModal from "@/features/shared/components/sections/ForwardForConfirmationModal.vue";
import {NavigationGuardNext} from "vue-router/types/router";
import DetailsLayout from "@/features/personProfile/components/DetailsLayout.vue";
import BaseDocumentApplicationUsageWidget from "@/features/documents/components/BaseDocumentApplicationUsageWidget.vue";
import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
import toasts from "@/components/Toasts";
import Badge from '@/features/shared/components/Badge.vue';

@Component({
    components: {
        HeadingGroup,
        SectionPageHeading,
        PracticeSeafaringDetailsContent,
        PracticeShipRepairDetailsContent,
        PracticeInlandVesselDetailsContent,
        PracticePilotageDetailsContent,
        PracticeVtsCenterDetailsContent,
        SectionDocumentMetaData,
        ValidityStatusBadge,
        ApprovalActions,
        RejectionDetails,
        ForwardForConfirmationModal,
        DetailsLayout,
        BaseDocumentApplicationUsageWidget,
        ButtonWithConfirm,
        Badge
    },
    beforeRouteEnter(to, from, next: NavigationGuardNext<PracticeDetails>) {
        to.meta!.props.id = to.params.id;
        to.meta!.props.seafarerId = to.params.seafarerId;
        next();
    }
})
export default class PracticeDetails extends Vue {
    @Prop({required: true})
    props!: PracticeDetailsProps;
    @Prop({required: true})
    id!: string;
    @Prop({required: true})
    seafarerId!: string;
    @Prop({default: true})
    addContainerCss!: boolean;

    actions: IPracticeActions = factory.create(PracticeActions);
    details: PracticeDetailsModel = factory.create(this.props.detailsType);

    forwardForConfirmationUrl = ``;

    dataLoaded = false;

    async created() {
        await this.refresh();
        this.dataLoaded = true;

        const forwardForConfirmationPath = this.$router.resolve({
            name: 'seafaring-practice-details',
            params: {
                seafarerId: this.seafarerId,
                id: this.id
            }
        });

        this.forwardForConfirmationUrl = `${window.location.protocol}//${window.location.host}${forwardForConfirmationPath.href}`;
    }

    async refresh() {
        this.details = await this.props.getDetails(this.id);
        await this.updateAvailableActions();
    }

    async approve() {
        await new PracticesClient().approve(this.details.id, this.details.version!);
        await this.refresh();
    }

    async disapprove(comment: string) {
        await new PracticesClient().disapprove(this.details.id, comment);
        await this.refresh();
    }

    async updateAvailableActions() {
        this.actions = await new PracticesClient().availableActions(this.details.id);
    }

    get practiceHeading() {
        switch (true) {
            case this.details instanceof ShipRepairPracticeDetailsModel:
                return this.$ls.Global_PracticeType_ShipRepairPractice;

            case this.details instanceof SeafaringPracticeDetailsModel:
                return this.$ls.Global_PracticeType_SeafaringPractice;

            case this.details instanceof InlandVesselPracticeDetailsModel:
                return this.$ls.Global_PracticeType_InlandVesselPractice || 'Siseveelaeva praktika';

            case this.details instanceof PilotagePracticeDetailsModel:
                return this.$ls.Global_PracticeType_PilotagePractice || 'Lootsipraktika';

            case this.details instanceof VtsCenterPracticeDetailsModel:
                return this.$ls.Global_PracticeType_VtsCenterPractice || 'Töö VTS keskuses';

            default:
                return "Unknown";
        }
    }

    get componentTypeName() {
        switch (true) {
            case this.details instanceof ShipRepairPracticeDetailsModel:
                return "PracticeShipRepairDetailsContent";

            case this.details instanceof SeafaringPracticeDetailsModel:
                return "PracticeSeafaringDetailsContent";

            case this.details instanceof InlandVesselPracticeDetailsModel:
                return "PracticeInlandVesselDetailsContent";

            case this.details instanceof PilotagePracticeDetailsModel:
                return "PracticePilotageDetailsContent";

            case this.details instanceof VtsCenterPracticeDetailsModel:
                return "PracticeVtsCenterDetailsContent";

            default:
                return "Unknown";
        }
    }

    async deletePractice() {
        await new PracticesClient()
            .delete(this.details.id)
            .then(async () => {
                toasts.success(this.$ls.Documents_Toast_Success_DocumentDeletion);
            })
            .catch((error: ResultError) => error.messagesFlattened.forEach(x => toasts.failure(x)));

        await this.$router.push({name: "seafarer-practices", params: { seafarerId: this.seafarerId }});
    }

    navigateToEdit(){
        this.$router.push({ name: this.props.editRouteName, params: { id: this.details.id, seafarerId: this.seafarerId }});
    }
}

